// 用户权限相关工具函数

/**
 * 从token中解析用户角色信息
 * @returns {Array} 用户角色数组
 */
export function getUserRoles() {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return [];
    }
    
    // 解析JWT token获取用户角色信息
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // 从token中提取角色信息
    // 角色信息可能在 'role', 'roles', 或 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role' 字段中
    let roles = [];
    
    if (payload.role) {
      roles = Array.isArray(payload.role) ? payload.role : [payload.role];
    } else if (payload.roles) {
      roles = Array.isArray(payload.roles) ? payload.roles : [payload.roles];
    } else if (payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
      const rolesClaim = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      roles = Array.isArray(rolesClaim) ? rolesClaim : [rolesClaim];
    }
    
    return roles.map(role => role.toLowerCase());
  } catch (error) {
    console.error('解析用户角色失败:', error);
    return [];
  }
}

/**
 * 检查用户是否有指定角色
 * @param {string|Array} requiredRoles - 需要的角色（字符串或数组）
 * @returns {boolean} 是否有权限
 */
export function hasRole(requiredRoles) {
  const userRoles = getUserRoles();
  
  if (!userRoles.length) {
    return false;
  }
  
  // 如果是字符串，转换为数组
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  
  // 检查用户是否有任一所需角色
  return roles.some(role => userRoles.includes(role.toLowerCase()));
}

export function getCurrentUserInfo() {
  try {
    const value = localStorage.getItem('userInfo');
    return value ? JSON.parse(value) : {};
  } catch (error) {
    console.error('读取用户信息失败:', error);
    return {};
  }
}

export function getCurrentMerchantId() {
  const userInfo = getCurrentUserInfo();
  return userInfo?.merchantId || userInfo?.MerchantId || null;
}

export function isSuperAdmin() {
  const merchantId = getCurrentMerchantId();
  return hasRole('super_admin') || (hasRole('admin') && !merchantId);
}

export function isCompanyAdmin() {
  const merchantId = getCurrentMerchantId();
  return hasRole(['company_admin', 'merchant']) || (hasRole('admin') && !!merchantId);
}

/**
 * 检查用户是否为管理员
 * @returns {boolean} 是否为管理员
 */
export function isAdmin() {
  // 管理员与商户权限等同
  return isSuperAdmin() || isCompanyAdmin();
}

/**
 * 检查用户是否为财务人员
 * @returns {boolean} 是否为财务人员
 */
export function isFinance() {
  return hasRole('finance');
}

/**
 * 检查用户是否为商户
 * @returns {boolean} 是否为商户
 */
export function isMerchant() {
  return hasRole('merchant');
}

/**
 * 检查用户是否为管理员或财务人员
 * @returns {boolean} 是否为管理员或财务人员
 */
export function isAdminOrFinance() {
  // 管理员与商户权限等同，财务单独权限
  return isAdmin() || hasRole('finance');
}

/**
 * 检查用户是否为管理员或商户
 * @returns {boolean} 是否为管理员或商户
 */
export function isAdminOrMerchant() {
  return isAdmin() || hasRole('merchant');
}

/**
 * 检查用户是否为管理员、财务人员或商户
 * @returns {boolean} 是否为管理员、财务人员或商户
 */
export function isAdminOrFinanceOrMerchant() {
  return isAdmin() || hasRole(['finance', 'merchant']);
}

/**
 * 检查菜单项是否应该显示
 * @param {string} menuKey - 菜单项的key
 * @returns {boolean} 是否应该显示
 */
export function shouldShowMenuItem(menuKey) {
  // 仅对明确的“特殊权限页”进行权限控制，其余默认显示
  // 管理员和财务权限的菜单项（需要权限）
  const adminFinanceMenus = [
    'company-finance',      // 公司财务报表
    'IncomeExpenseList',    // 收支记录管理
    'fund-flows',           // 资金流管理
    'BankAccountList',      // 银行账户管理
    'IncomeExpenseTypeList' // 收支类型管理
  ];

  // 只有管理员可见的菜单项（需要权限）
  const adminOnlyMenus = [
    'UserManagement',    // 用户管理
    'UserRoles',         // 角色权限
    'system-params',     // 系统参数
    'DataAuthorization',  // 数据授权
    'SalesmanManagement',
    'WarehouseManagement',
    'MessageConfig',
    'ApprovalWorkflowList',
    'ConfigManager'
  ];

  // 仅校验明确限制的菜单，其余默认显示给所有角色（含员工）
  const platformOnlyMenus = [
    'PlatformCompanyManagement'
  ];

  if (platformOnlyMenus.includes(menuKey)) {
    return isSuperAdmin();
  }

  if (adminOnlyMenus.includes(menuKey)) {
    return isAdmin();
  }

  if (adminFinanceMenus.includes(menuKey)) {
    return isAdminOrFinance();
  }

  // 其他菜单项默认显示（包括商品管理、仓库管理、报表等）
  return true;
}
